import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  CircleDashed,
  Loader2,
  MessageSquareText,
  RefreshCw,
  Search,
  Settings2,
  Sparkles,
  TriangleAlert,
  X,
} from "lucide-react";
import {
  DropshipV2RequestError,
  dropshipV2ErrorMessage,
  type DropshipV2AIBuild,
  type DropshipV2AIConversation,
  type DropshipV2AILineAgent,
  type DropshipV2AIProductMatch,
  type DropshipV2ClarificationQuestion,
  type DropshipV2Line,
  useAnswerDropshipV2Clarifications,
  useApplyDropshipV2AIAlternative,
  useCreateDropshipV2AIConversation,
  useDropshipV2AIConversation,
  useDropshipV2Clarifications,
  useRetryDropshipV2Processing,
} from "@/api/dropship-v2";
import { AgentComposer } from "@/components/agent/agent-composer";
import { AgentMessages } from "@/components/agent/agent-messages";
import { LoadingButton } from "@/components/loading-button";
import { POLineItemProductCell } from "@/components/po-line-item-product-cell";
import { ReviewChoiceGroup } from "@/components/review-choice-group";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  storedMessagesToUi,
  useAgentChat,
  type UIMessage,
} from "@/hooks/use-agent-chat";
import { queryKeys } from "@/api/queries";
import {
  cn,
  formatMoney,
  formatPercent,
  formatStatus,
  parseUTCDate,
} from "@/lib/utils";
import { ComposeTextarea } from "@/components/compose-field";

const MAIN_SUGGESTIONS = [
  "Which equipment lines still need review?",
  "Summarize the PDF annotations and comments.",
  "Explain the lowest-confidence matches.",
];

const LINE_SUGGESTIONS = [
  "Why was this product selected?",
  "Compare the alternatives for this line.",
  "Which configurations or accessories need review?",
];

const MUTATING_QUOTE_TOOLS = new Set([
  "set_quote_draft_field",
  "add_quote_line",
  "update_quote_line",
  "remove_quote_line",
  "apply_quote_recommended_alternative",
]);

type QuoteLineAgentStatus =
  "queued" | "searching" | "reviewing" | "configuring" | "completed" | "failed";

const LINE_STATUS_LABELS: Record<QuoteLineAgentStatus, string> = {
  queued: "Queued",
  searching: "Searching",
  reviewing: "Reviewing",
  configuring: "Configuring",
  completed: "Completed",
  failed: "Failed",
};

function buildStageLabel(build: DropshipV2AIBuild) {
  if (build.terminal) {
    if (build.stage === "cancelled" || build.status === "cancelled")
      return "Build cancelled";
    if (build.stage === "completed" || build.status === "succeeded")
      return "Build complete";
    return "Build stopped";
  }
  if (build.retrying || build.stage === "retrying")
    return "Retrying automatically";
  if (build.stage === "failed") return "Preparing automatic retry";
  if (build.stage === "completed") return "Finalizing draft";
  if (build.stage === "extracting") return "Reading annotations";
  if (build.stage === "interpreting") return "Interpreting equipment";
  if (build.stage === "configuring") return "Checking configurations";
  if (build.stage === "finalizing") return "Finalizing draft";
  if (build.stage === "matching") return "Matching equipment";
  return "Queued";
}

function normalizedLineStatus(
  agent: DropshipV2AILineAgent,
): QuoteLineAgentStatus {
  if (agent.status === "failed" || agent.phase === "failed") return "failed";
  if (
    agent.status === "succeeded" ||
    agent.status === "completed" ||
    agent.phase === "completed"
  ) {
    return "completed";
  }
  if (
    agent.status === "pending" ||
    agent.status === "queued" ||
    agent.phase === "queued"
  ) {
    return "queued";
  }
  if (agent.status === "reviewing" || agent.phase === "opus_selection")
    return "reviewing";
  if (agent.status === "configuring" || agent.phase === "configuring")
    return "configuring";
  return "searching";
}

function useMediaQuery(query: string) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );

  useEffect(() => {
    const media = window.matchMedia(query);
    const update = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);

  return isMobile;
}

function useIsMobile() {
  return useMediaQuery("(max-width: 767px)");
}

function conversationMessages(
  conversation: DropshipV2AIConversation,
): UIMessage[] {
  return storedMessagesToUi(conversation.messages);
}

const CLARIFICATION_STATE_LABELS: Record<
  DropshipV2ClarificationQuestion["workflow_state"],
  string
> = {
  awaiting_answer: "Awaiting answer",
  queued: "Answer queued",
  running: "Applying answer",
  retrying: "Retrying automatically",
  resolved: "Resolved",
  failed: "Needs attention",
  stale: "Outdated",
};

function ClarificationCards({
  questions,
  contentRevision,
  lines,
  loading,
  error,
  draftUuid,
  answerDrafts,
  answerErrors,
  focusQuestionUuid,
  onAnswerDraftChange,
  onAnswerErrorChange,
  onQuestionFocused,
  onSelectedLineChange,
}: {
  questions: DropshipV2ClarificationQuestion[];
  contentRevision?: number;
  lines: DropshipV2Line[];
  loading: boolean;
  error: unknown;
  draftUuid: string;
  answerDrafts: Record<string, string>;
  answerErrors: Record<string, string>;
  focusQuestionUuid?: string | null;
  onAnswerDraftChange: (key: string, value?: string) => void;
  onAnswerErrorChange: (key: string, value?: string) => void;
  onQuestionFocused?: () => void;
  onSelectedLineChange?: (line: DropshipV2Line | null) => void;
}) {
  const answerClarifications = useAnswerDropshipV2Clarifications(draftUuid);

  useEffect(() => {
    if (!focusQuestionUuid || loading) return;
    const question = questions.find((item) => item.uuid === focusQuestionUuid);
    if (!question) return;
    const target = document.getElementById(
      `dropship-clarification-${question.uuid}`,
    );
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    target.focus({ preventScroll: true });
    onQuestionFocused?.();
  }, [focusQuestionUuid, loading, onQuestionFocused, questions]);

  if (loading && questions.length === 0) {
    return (
      <div
        className="rounded-lg border bg-muted/20 px-3 py-3 text-xs text-muted-foreground"
        role="status"
      >
        Checking for quote questions...
      </div>
    );
  }

  if (error && questions.length === 0) {
    return (
      <div
        className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-3 text-xs text-destructive"
        role="alert"
      >
        {dropshipV2ErrorMessage(
          error,
          "Quote questions could not be loaded. They will retry automatically.",
        )}
      </div>
    );
  }

  if (questions.length === 0) return null;

  return (
    <div className="space-y-3" aria-label="Structured quote clarifications">
      {questions.map((question) => {
        const draftKey = `${draftUuid}:${question.uuid}:${question.detected_revision}`;
        const answer = answerDrafts[draftKey] ?? question.answer ?? "";
        const targetLine = question.line_uuid
          ? lines.find((line) => line.uuid === question.line_uuid)
          : undefined;
        const canAnswer =
          question.workflow_state === "awaiting_answer" ||
          question.workflow_state === "failed";
        const pending =
          answerClarifications.isPending &&
          answerClarifications.variables?.answers.some(
            (item) => item.question_uuid === question.uuid,
          );
        const target = question.target_field ?? question.target_decision;
        const updateAnswer = (value: string) => {
          onAnswerDraftChange(draftKey, value);
          onAnswerErrorChange(draftKey);
        };
        const submit = () => {
          const trimmed = answer.trim();
          if (!trimmed || contentRevision == null) return;
          onAnswerErrorChange(draftKey);
          answerClarifications.mutate(
            {
              expected_content_revision: contentRevision,
              conversation_uuid: question.main_conversation_uuid,
              answers: [
                {
                  question_uuid: question.uuid,
                  expected_detected_revision: question.detected_revision,
                  answer: trimmed,
                },
              ],
            },
            {
              onSuccess: () => {
                onAnswerDraftChange(draftKey);
                onAnswerErrorChange(draftKey);
              },
              onError: (mutationError) =>
                onAnswerErrorChange(
                  draftKey,
                  dropshipV2ErrorMessage(
                    mutationError,
                    "The answer was not submitted. Review it and try again.",
                  ),
                ),
            },
          );
        };

        return (
          <article
            id={`dropship-clarification-${question.uuid}`}
            key={question.uuid}
            tabIndex={-1}
            className="rounded-lg border bg-background p-3"
            aria-labelledby={`dropship-clarification-title-${question.uuid}`}
          >
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant={question.blocking ? "destructive" : "outline"}>
                {question.blocking ? "Blocking" : "Advisory"}
              </Badge>
              <Badge variant="secondary">
                {question.scope === "line" ? "Line scope" : "Draft scope"}
              </Badge>
              <span
                className="ml-auto text-[11px] font-medium text-muted-foreground"
                role="status"
              >
                {CLARIFICATION_STATE_LABELS[question.workflow_state]}
              </span>
            </div>
            <h3
              id={`dropship-clarification-title-${question.uuid}`}
              className="mt-2 text-sm font-semibold leading-5"
            >
              {question.question}
            </h3>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              {question.reason}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
              {target ? <span>Decision: {formatStatus(target)}</span> : null}
              {question.scope === "line" ? (
                targetLine ? (
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto min-h-0 p-0 text-[11px]"
                    onClick={() => onSelectedLineChange?.(targetLine)}
                  >
                    Review line {targetLine.display_number}
                    {targetLine.model ? ` · ${targetLine.model}` : ""}
                  </Button>
                ) : (
                  <span>Target line unavailable in the loaded results</span>
                )
              ) : null}
            </div>

            {canAnswer ? (
              <div className="mt-3 space-y-2">
                {question.answer_type === "text" ? (
                  <ComposeTextarea
                    aria-label={`Answer: ${question.question}`}
                    value={answer}
                    disabled={pending}
                    onChange={(event) => updateAnswer(event.target.value)}
                    placeholder="Enter the confirmed detail"
                    className="min-h-20 resize-y text-sm"
                  />
                ) : (
                  <ReviewChoiceGroup
                    label={`Answer: ${question.question}`}
                    value={answer}
                    options={(question.answer_type === "yes_no"
                      ? ["Yes", "No"]
                      : question.options
                    ).map((option) => ({ value: option, label: option }))}
                    onChange={updateAnswer}
                    disabled={pending}
                    className="w-full"
                    optionClassName="min-h-9 flex-1 justify-center"
                  />
                )}
                {answerErrors[draftKey] ? (
                  <p role="alert" className="text-xs text-destructive">
                    {answerErrors[draftKey]}
                  </p>
                ) : null}
                {question.workflow_state === "failed" &&
                question.processing_error ? (
                  <p className="text-xs text-destructive">
                    {question.processing_error}
                  </p>
                ) : null}
                <LoadingButton
                  type="button"
                  size="sm"
                  loading={Boolean(pending)}
                  loadingText="Submitting answer..."
                  disabled={
                    !answer.trim() ||
                    contentRevision == null ||
                    answerClarifications.isPending
                  }
                  onClick={submit}
                >
                  {question.workflow_state === "failed"
                    ? "Retry answer"
                    : "Submit answer"}
                </LoadingButton>
              </div>
            ) : (
              <div className="mt-3 rounded-md border bg-muted/20 px-3 py-2 text-xs">
                {question.answer ? <p>Answer: {question.answer}</p> : null}
                {question.workflow_state === "queued" ? (
                  <p>Your answer is queued for processing.</p>
                ) : null}
                {question.workflow_state === "running" ? (
                  <p>The agent is applying this answer.</p>
                ) : null}
                {question.workflow_state === "retrying" ? (
                  <p>
                    The task is retrying automatically after{" "}
                    {question.task_retries} failed attempt
                    {question.task_retries === 1 ? "" : "s"}.
                  </p>
                ) : null}
                {question.workflow_state === "resolved" ? (
                  <p>This clarification has been resolved.</p>
                ) : null}
                {question.workflow_state === "stale" ? (
                  <p>
                    This question belongs to an older draft revision and can no
                    longer be answered.
                  </p>
                ) : null}
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}

function BoundContextualChat({
  conversation,
  lineScoped,
  supplemental,
}: {
  conversation: DropshipV2AIConversation;
  lineScoped: boolean;
  supplemental?: ReactNode;
}) {
  const queryClient = useQueryClient();
  const initialMessages = useMemo(
    () => conversationMessages(conversation),
    [conversation],
  );
  const { messages, send, cancel, isStreaming, isConnected } = useAgentChat({
    conversationUuid: conversation.uuid,
    initialMessages,
    onToolResult: (event) => {
      if (!event.ok || !MUTATING_QUOTE_TOOLS.has(event.name)) return;
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropshipV2.all,
      });
    },
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-9 items-center justify-between gap-2 border-b px-3 text-xs text-muted-foreground">
        <span>
          Full source document and build context stay with{" "}
          {lineScoped ? "this equipment line" : "this draft"}.
        </span>
        <span role="status" className="flex shrink-0 items-center gap-1.5">
          <span
            className={cn(
              "size-1.5 rounded-full",
              isConnected ? "bg-usa-blue" : "bg-amber-500",
            )}
            aria-hidden="true"
          />
          {isConnected ? "Connected" : "Reconnecting"}
        </span>
      </div>
      <AgentMessages
        messages={messages}
        isStreaming={isStreaming}
        onSuggestionClick={send}
        disabled={!isConnected}
        emptyTitle={
          lineScoped ? "Ask about this match" : "Ask about this quote"
        }
        emptyDescription={
          lineScoped
            ? "The line agent can explain evidence, alternatives, and configuration choices."
            : "The quote agent keeps the source file, line work, and review state in context."
        }
        suggestions={lineScoped ? LINE_SUGGESTIONS : MAIN_SUGGESTIONS}
        supplemental={supplemental}
      />
      <AgentComposer
        onSend={send}
        onCancel={cancel}
        disabled={!isConnected}
        isStreaming={isStreaming}
        conversationUuid={conversation.uuid}
        placeholder={
          lineScoped ? "Ask about this line..." : "Ask about this quote..."
        }
      />
    </div>
  );
}

function ContextualAgentChat({
  draftUuid,
  lineUuid,
  conversationUuid,
  enabled,
  refreshKey,
  lines = [],
  answerDrafts = {},
  answerErrors = {},
  focusClarificationUuid,
  onAnswerDraftChange,
  onAnswerErrorChange,
  onClarificationFocused,
  onSelectedLineChange,
}: {
  draftUuid: string;
  lineUuid?: string | null;
  conversationUuid?: string | null;
  enabled: boolean;
  refreshKey?: string | null;
  lines?: DropshipV2Line[];
  answerDrafts?: Record<string, string>;
  answerErrors?: Record<string, string>;
  focusClarificationUuid?: string | null;
  onAnswerDraftChange?: (key: string, value?: string) => void;
  onAnswerErrorChange?: (key: string, value?: string) => void;
  onClarificationFocused?: () => void;
  onSelectedLineChange?: (line: DropshipV2Line | null) => void;
}) {
  const createConversation = useCreateDropshipV2AIConversation(draftUuid);
  const createAttempted = useRef<string | null>(null);
  const scopeKey = `${draftUuid}:${lineUuid ?? "main"}`;
  const conversationQuery = useDropshipV2AIConversation(
    draftUuid,
    lineUuid,
    enabled,
  );
  const clarificationQuery = useDropshipV2Clarifications(draftUuid, enabled);
  const actionableQuestions = (clarificationQuery.data?.questions ?? []).filter(
    (question) =>
      question.status === "open" &&
      !["resolved", "stale"].includes(question.workflow_state),
  );
  const conversation = conversationQuery.data ?? createConversation.data;
  const refetchConversation = conversationQuery.refetch;
  const clarificationRefreshKey = lineUuid
    ? clarificationQuery.data?.questions
        .filter((question) => question.line_uuid === lineUuid)
        .map((question) => `${question.uuid}:${question.updated_at}`)
        .join("|")
    : null;
  const combinedRefreshKey = `${refreshKey ?? ""}:${clarificationRefreshKey ?? ""}`;

  useEffect(() => {
    if (!enabled || !refreshKey || typeof refetchConversation !== "function") {
      return;
    }
    void refetchConversation();
  }, [combinedRefreshKey, enabled, refetchConversation, refreshKey]);

  useEffect(() => {
    if (
      !enabled ||
      !conversationQuery.isSuccess ||
      conversation ||
      createConversation.isPending ||
      createAttempted.current === scopeKey
    ) {
      return;
    }
    createAttempted.current = scopeKey;
    void createConversation.mutateAsync({ lineUuid }).catch(() => undefined);
  }, [
    conversation,
    conversationQuery.isSuccess,
    createConversation,
    enabled,
    lineUuid,
    scopeKey,
  ]);

  if (!enabled) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-5 py-8 text-center">
        <p className="text-sm font-medium">No quote-agent context yet</p>
        <p className="mt-1 max-w-xs text-xs text-muted-foreground">
          Upload a PDF or image to start a contextual build and conversation for
          this draft.
        </p>
      </div>
    );
  }

  if (conversation) {
    return (
      <BoundContextualChat
        key={conversation.uuid}
        conversation={conversation}
        lineScoped={Boolean(lineUuid)}
        supplemental={
          lineUuid ? undefined : (
            <ClarificationCards
              draftUuid={draftUuid}
              questions={actionableQuestions}
              contentRevision={clarificationQuery.data?.content_revision}
              lines={lines}
              loading={clarificationQuery.isLoading}
              error={clarificationQuery.error}
              answerDrafts={answerDrafts}
              answerErrors={answerErrors}
              focusQuestionUuid={focusClarificationUuid}
              onAnswerDraftChange={onAnswerDraftChange ?? (() => undefined)}
              onAnswerErrorChange={onAnswerErrorChange ?? (() => undefined)}
              onQuestionFocused={onClarificationFocused}
              onSelectedLineChange={onSelectedLineChange}
            />
          )
        }
      />
    );
  }

  if (conversationQuery.isLoading || createConversation.isPending) {
    return (
      <div className="flex flex-1 flex-col gap-3 p-4" aria-busy="true">
        <p role="status" className="sr-only">
          Loading conversation...
        </p>
        <Skeleton className="h-16 w-5/6" />
        <Skeleton className="ml-auto h-12 w-3/5" />
        <Skeleton className="mt-auto h-20 w-full" />
      </div>
    );
  }

  const error = createConversation.error ?? conversationQuery.error;
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-5 py-8 text-center">
      <AlertCircle className="size-5 text-destructive" aria-hidden="true" />
      <div>
        <p className="text-sm font-medium">Conversation unavailable</p>
        <p role="alert" className="mt-1 text-xs text-muted-foreground">
          {dropshipV2ErrorMessage(
            error,
            "The contextual chat could not be started.",
          )}
        </p>
      </div>
      <LoadingButton
        type="button"
        variant="outline"
        className="min-h-11 sm:min-h-9"
        loading={createConversation.isPending}
        loadingText="Starting..."
        onClick={() => {
          if (conversationUuid || conversationQuery.data) {
            void conversationQuery.refetch();
          } else {
            createAttempted.current = scopeKey;
            void createConversation
              .mutateAsync({ lineUuid })
              .catch(() => undefined);
          }
        }}
      >
        <MessageSquareText className="size-4" /> Retry conversation
      </LoadingButton>
    </div>
  );
}

function LineStatusIcon({ status }: { status: QuoteLineAgentStatus }) {
  if (status === "completed") {
    return (
      <CheckCircle2 aria-hidden="true" className="size-3.5 text-usa-blue" />
    );
  }
  if (status === "failed") {
    return (
      <AlertCircle aria-hidden="true" className="size-3.5 text-destructive" />
    );
  }
  if (status === "searching") {
    return <Search aria-hidden="true" className="size-3.5 text-sky-600" />;
  }
  if (status === "configuring") {
    return (
      <Settings2 aria-hidden="true" className="size-3.5 text-violet-600" />
    );
  }
  if (status === "reviewing") {
    return (
      <TriangleAlert aria-hidden="true" className="size-3.5 text-amber-600" />
    );
  }
  return (
    <CircleDashed
      aria-hidden="true"
      className="size-3.5 text-muted-foreground"
    />
  );
}

function durationMilliseconds(
  start: string | null | undefined,
  end: string | null | undefined,
) {
  if (!start || !end) return null;
  const duration = parseUTCDate(end).getTime() - parseUTCDate(start).getTime();
  return Number.isFinite(duration) && duration >= 0 ? duration : null;
}

function formatDuration(milliseconds: number | null) {
  if (milliseconds == null) return "—";
  const seconds = Math.round(milliseconds / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

function QuoteBuildProgress({
  draftUuid,
  build,
  lines,
  loading,
  error,
  onReviewRetainedEquipment,
}: {
  draftUuid: string;
  build: DropshipV2AIBuild | null | undefined;
  lines: DropshipV2Line[];
  loading: boolean;
  error: unknown;
  onReviewRetainedEquipment?: () => void;
}) {
  const retry = useRetryDropshipV2Processing(draftUuid);
  const [retryError, setRetryError] = useState<string | null>(null);

  if (loading && !build) {
    return (
      <section
        aria-labelledby="quote-build-progress-title"
        aria-busy="true"
        className="space-y-3 border-b p-4"
      >
        <h2 id="quote-build-progress-title" className="text-sm font-semibold">
          Build progress
        </h2>
        <p role="status" className="sr-only">
          Loading build progress...
        </p>
        <Skeleton className="h-2 w-full" />
        <Skeleton className="h-12 w-full" />
      </section>
    );
  }

  if (!build) {
    return (
      <section
        aria-labelledby="quote-build-progress-title"
        className="border-b px-4 py-3"
      >
        <h2 id="quote-build-progress-title" className="text-sm font-semibold">
          Build progress
        </h2>
        <p
          className={cn(
            "mt-1 text-xs",
            error ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {error
            ? "Build status is unavailable. Conversation is unavailable until build context loads."
            : "No source build has been started for this draft."}
        </p>
      </section>
    );
  }

  const progress = Math.max(0, Math.min(100, build.progress));
  const total = build.counts.total || lines.length;
  const failed = build.counts.failed;
  const statusItems: Array<{
    status: QuoteLineAgentStatus;
    count: number;
    barClassName: string;
  }> = [
    {
      status: "queued",
      count: build.counts.queued,
      barClassName: "bg-muted-foreground/25",
    },
    {
      status: "searching",
      count: build.counts.searching,
      barClassName: "bg-sky-500",
    },
    {
      status: "reviewing",
      count: build.counts.reviewing,
      barClassName: "bg-amber-500",
    },
    {
      status: "configuring",
      count: build.counts.configuring,
      barClassName: "bg-violet-500",
    },
    {
      status: "completed",
      count: build.counts.completed,
      barClassName: "bg-usa-blue",
    },
    {
      status: "failed",
      count: build.counts.failed,
      barClassName: "bg-destructive",
    },
  ];
  const timingEnd =
    build.completed_at ?? build.heartbeat_at ?? build.updated_at;
  const settled = build.terminal;
  const terminalFailure =
    build.terminal &&
    build.stage !== "completed" &&
    build.status !== "succeeded" &&
    build.stage !== "cancelled" &&
    build.status !== "cancelled";
  const failedLineAgents = build.line_agents.filter(
    (agent) => normalizedLineStatus(agent) === "failed",
  );
  const timingItems = [
    {
      label: "Queue",
      value: formatDuration(
        durationMilliseconds(build.created_at, build.started_at ?? timingEnd),
      ),
    },
    {
      label: "Processing",
      value: formatDuration(durationMilliseconds(build.started_at, timingEnd)),
    },
    {
      label: build.completed_at ? "Accepted to ready" : "Elapsed",
      value: formatDuration(durationMilliseconds(build.created_at, timingEnd)),
    },
  ];

  return (
    <section aria-labelledby="quote-build-progress-title" className="border-b">
      <div className="space-y-3 px-4 py-3" aria-live="polite">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2
              id="quote-build-progress-title"
              className="text-sm font-semibold"
            >
              Build progress
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {buildStageLabel(build)}
            </p>
          </div>
          <span className="text-xs font-medium tabular-nums">{progress}%</span>
        </div>
        <Progress
          value={progress}
          aria-label={`Quote build ${progress}% complete`}
        />
        <dl
          aria-label="Build timing"
          className="grid grid-cols-3 gap-2 border-t pt-3"
        >
          {timingItems.map(({ label, value }) => (
            <div key={label} className="min-w-0">
              <dt className="truncate text-[11px] text-muted-foreground">
                {label}
              </dt>
              <dd className="mt-0.5 text-xs font-semibold tabular-nums">
                {value}
              </dd>
            </div>
          ))}
        </dl>
        {!build.terminal && build.retrying ? (
          <p
            role="status"
            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
          >
            Automatic retry is in progress. Task attempt {build.task_attempt};{" "}
            {build.task_retries}{" "}
            {build.task_retries === 1 ? "retry" : "retries"} recorded. Draft
            work remains available.
          </p>
        ) : terminalFailure ? (
          <section
            aria-labelledby="quote-build-failure-title"
            className="space-y-3 rounded-md border border-destructive/30 bg-destructive/5 p-3"
          >
            <div>
              <h3
                id="quote-build-failure-title"
                className="text-sm font-semibold text-red-700 dark:text-red-300"
              >
                Build stopped with {failed} failed row
                {failed === 1 ? "" : "s"}
              </h3>
              <p className="mt-1 text-xs text-foreground/70">
                {build.counts.completed} completed row
                {build.counts.completed === 1 ? " was" : "s were"} retained
                in the draft.
              </p>
            </div>
            {failedLineAgents.length > 0 ? (
              <ul
                aria-label="Failed equipment rows"
                className="max-h-48 space-y-2 overflow-y-auto overscroll-contain pr-1"
              >
                {failedLineAgents.map((agent) => (
                  <li
                    key={agent.line_uuid}
                    className="rounded-md border border-destructive/20 bg-background px-3 py-2"
                  >
                    <p className="text-xs font-semibold text-foreground">
                      Row {agent.display_number || "unknown"} ·{" "}
                      {agent.manufacturer_name || "Manufacturer unknown"} ·{" "}
                      {agent.model || "Model unknown"}
                    </p>
                    <p className="mt-1 text-xs leading-5 text-destructive">
                      {agent.error ||
                        agent.error_message ||
                        "This row could not be completed."}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-destructive">
                {build.error ||
                  build.error_message ||
                  "The build stopped before it could finish."}
              </p>
            )}
            {retryError ? (
              <p role="alert" className="text-xs text-destructive">
                {retryError}
              </p>
            ) : null}
            <div className="flex flex-col gap-2 sm:flex-row">
              <LoadingButton
                type="button"
                variant="outline"
                className="min-h-11 w-full sm:min-h-9 sm:w-auto"
                loading={retry.isPending}
                loadingText="Retrying failed rows..."
                onClick={() => {
                  setRetryError(null);
                  retry.mutate(undefined, {
                    onSuccess: () => setRetryError(null),
                    onError: (mutationError) =>
                      setRetryError(
                        dropshipV2ErrorMessage(
                          mutationError,
                          "Failed rows could not be retried.",
                        ),
                      ),
                  });
                }}
              >
                Retry failed rows ({failed})
              </LoadingButton>
              {onReviewRetainedEquipment ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="min-h-11 w-full sm:min-h-9 sm:w-auto"
                  onClick={onReviewRetainedEquipment}
                >
                  Review retained equipment
                </Button>
              ) : null}
            </div>
          </section>
        ) : failed > 0 ? (
          <p
            role="status"
            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
          >
            {failed} line{failed === 1 ? "" : "s"} need manual attention.
            Completed matches remain available.
          </p>
        ) : null}
        {error ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
          >
            Build status could not be refreshed. Showing the last known build
            data.
          </p>
        ) : null}
      </div>

      <div className="space-y-3 border-t px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-xs font-semibold">Equipment agents</h3>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              {total} line{total === 1 ? "" : "s"} moving through catalog review
            </p>
          </div>
          <span className="text-xs text-muted-foreground">
            {error
              ? "Last known"
              : loading
                ? "Refreshing"
                : settled
                  ? "Final"
                  : "Live"}
          </span>
        </div>
        <div
          role="img"
          aria-label={statusItems
            .map(
              ({ status, count }) =>
                `${count} ${LINE_STATUS_LABELS[status].toLowerCase()}`,
            )
            .join(", ")}
          className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted"
        >
          {total > 0
            ? statusItems.map(({ status, count, barClassName }) =>
                count > 0 ? (
                  <span
                    key={status}
                    className={barClassName}
                    style={{ width: `${(count / total) * 100}%` }}
                  />
                ) : null,
              )
            : null}
        </div>
        <ul
          aria-label="Equipment agent status legend"
          className="grid grid-cols-3 gap-x-4 gap-y-2"
        >
          {statusItems.map(({ status, count }) => (
            <li key={status} className="flex min-w-0 items-center gap-1.5">
              <LineStatusIcon status={status} />
              <span className="min-w-0">
                <span className="block truncate text-[10px] text-muted-foreground">
                  {LINE_STATUS_LABELS[status]}
                </span>
                <span className="block text-xs font-semibold tabular-nums">
                  {count}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function confidenceLabel(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return "—";
  return formatPercent(value <= 1 ? value * 100 : value);
}

function moneyLabel(value: string | null | undefined) {
  if (value == null || value === "" || !Number.isFinite(Number(value)))
    return "—";
  return formatMoney(value);
}

function percentLabel(value: string | number | null | undefined) {
  if (value == null || value === "" || !Number.isFinite(Number(value)))
    return "—";
  return formatPercent(value);
}

function comparisonValue(value: string | null | undefined) {
  return value?.trim() || "Not specified";
}

function matchKey(match: DropshipV2AIProductMatch) {
  return match.uuid;
}

function MatchDisclosure({
  label,
  context,
  children,
}: {
  label: string;
  context?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const disclosureId = useId();
  const triggerId = `${disclosureId}-trigger`;
  const panelId = `${disclosureId}-panel`;

  return (
    <div>
      <Button
        id={triggerId}
        type="button"
        variant="ghost"
        size="sm"
        className="min-h-11 w-full justify-between px-0 text-xs font-medium hover:bg-transparent sm:min-h-9"
        aria-label={context ? `${label} for ${context}` : label}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{label}</span>
        <ChevronDown
          className={cn("size-4 shrink-0", open && "rotate-180")}
          aria-hidden="true"
        />
      </Button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        hidden={!open}
        className="pb-2 text-xs"
      >
        {children}
      </div>
    </div>
  );
}

function ProductId({ match }: { match: DropshipV2AIProductMatch }) {
  if (match.product_id == null) return null;

  return (
    <p
      aria-label={`Product ID ${match.product_id}`}
      className="mt-1 flex items-baseline gap-1.5 text-[11px] text-muted-foreground"
    >
      <span>Product ID</span>
      <span className="font-mono font-medium tabular-nums text-foreground">
        {match.product_id}
      </span>
    </p>
  );
}

function FacetDifferences({ match }: { match: DropshipV2AIProductMatch }) {
  if (!match.facet_differences.length) return null;

  return (
    <section
      aria-label={`Key specification differences for ${match.model}`}
      className="mt-3 border-t pt-3"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h4 className="font-medium">Key differences</h4>
        <span className="text-[10px] text-muted-foreground">
          Selected → This option
        </span>
      </div>
      <div
        role="table"
        aria-label="Selected product compared with this option"
        className="mt-1 divide-y"
      >
        {match.facet_differences.map((difference) => (
          <div
            key={`${difference.facet}-${difference.selected_value}-${difference.alternative_value}`}
            role="row"
            className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-x-2 py-1.5"
          >
            <span
              role="rowheader"
              className="col-span-3 mb-0.5 break-words text-[10px] font-medium text-muted-foreground"
            >
              {formatStatus(difference.facet)}
            </span>
            <span
              role="cell"
              className="break-words leading-4 text-muted-foreground"
            >
              {comparisonValue(difference.selected_value)}
            </span>
            <span
              className="leading-4 text-muted-foreground"
              aria-hidden="true"
            >
              →
            </span>
            <span role="cell" className="break-words font-medium leading-4">
              {comparisonValue(difference.alternative_value)}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function ReviewFlags({ flags }: { flags: string[] }) {
  if (!flags.length) return null;

  return (
    <div
      className="mt-3 flex flex-wrap items-center gap-1.5"
      aria-label="Review flags"
    >
      <span className="mr-0.5 text-[11px] font-medium text-muted-foreground">
        Review
      </span>
      {flags.map((flag) => (
        <span
          key={flag}
          className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
        >
          {flag}
        </span>
      ))}
    </div>
  );
}

function CurrentRecommendations({
  match,
}: {
  match: DropshipV2AIProductMatch;
}) {
  const groups = [
    { label: "Configuration", items: match.configuration_recommendations },
    { label: "Accessories", items: match.accessory_recommendations },
  ].filter(({ items }) => items.length);

  if (!groups.length) return null;

  return (
    <section
      aria-labelledby={`recommendations-${match.uuid}`}
      className="mt-3 border-t pt-3"
    >
      <h4 id={`recommendations-${match.uuid}`} className="text-xs font-medium">
        Current recommendations
      </h4>
      <div className="mt-2 grid gap-x-4 gap-y-2 sm:grid-cols-2">
        {groups.map(({ label, items }) => (
          <div key={label} className="min-w-0">
            <p className="text-[11px] font-medium text-muted-foreground">
              {label}
            </p>
            <ul className="mt-1 space-y-1 text-xs">
              {items.map((item) => (
                <li key={item} className="break-words leading-4">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function SelectedMatchSummary({ match }: { match: DropshipV2AIProductMatch }) {
  const pricingFacts = [
    { label: "Sell", value: moneyLabel(match.sell_price), emphasized: true },
    { label: "Net", value: moneyLabel(match.net_price), emphasized: true },
    {
      label: "Margin",
      value: percentLabel(match.margin_percent),
      emphasized: true,
    },
    { label: "List", value: moneyLabel(match.list_price), emphasized: false },
    {
      label: "Discount",
      value: match.discount_chain?.trim() || "—",
      emphasized: false,
    },
    {
      label: "Fit confidence",
      value: confidenceLabel(match.confidence),
      emphasized: false,
    },
  ];
  const hasSelectionEvidence = Boolean(
    match.reason || match.facet_differences.length,
  );

  return (
    <article
      aria-label={`Selected product ${match.model}`}
      className="rounded-lg border border-usa-blue/30 bg-usa-blue/5 p-3 dark:border-usa-blue/50 dark:bg-usa-blue/10"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold">Selected match</span>
        <Badge variant="outline">Current</Badge>
      </div>
      <POLineItemProductCell
        model={match.model}
        description={match.description}
        sku={match.sku}
        productUuid={match.product_uuid}
        imageUrl={match.image_url}
        manufacturerName={match.manufacturer_name}
        compact
      />
      <ProductId match={match} />
      <dl
        aria-label={`Catalog pricing and fit for ${match.model}`}
        className="mt-3 grid grid-cols-3 gap-x-2 gap-y-3 border-t pt-3 text-xs"
      >
        {pricingFacts.map(({ label, value, emphasized }) => (
          <div key={label} className="min-w-0">
            <dt className="text-[11px] text-muted-foreground">{label}</dt>
            <dd
              className={cn(
                "mt-0.5 break-words tabular-nums",
                emphasized ? "font-semibold text-foreground" : "font-medium",
              )}
            >
              {value}
            </dd>
          </div>
        ))}
      </dl>
      <ReviewFlags flags={match.review_flags} />
      <CurrentRecommendations match={match} />
      {hasSelectionEvidence ? (
        <div className="mt-2 border-t">
          <MatchDisclosure label="Why this product was selected">
            {match.reason ? (
              <p className="whitespace-pre-wrap break-words leading-5 text-muted-foreground">
                {match.reason}
              </p>
            ) : null}
            <FacetDifferences match={match} />
          </MatchDisclosure>
        </div>
      ) : null}
    </article>
  );
}

function AlternativeMatchRow({
  match,
  pending,
  disabled,
  onApply,
}: {
  match: DropshipV2AIProductMatch;
  pending: boolean;
  disabled: boolean;
  onApply: () => void;
}) {
  const scanFacts = [
    { label: "Sell", value: moneyLabel(match.sell_price) },
    { label: "Net", value: moneyLabel(match.net_price) },
    { label: "Margin", value: percentLabel(match.margin_percent) },
    { label: "Fit", value: confidenceLabel(match.confidence) },
  ];

  return (
    <article aria-label={`Alternative product ${match.model}`} className="p-3">
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
        <div className="min-w-0">
          <POLineItemProductCell
            model={match.model}
            description={match.description}
            sku={match.sku}
            productUuid={match.product_uuid}
            imageUrl={match.image_url}
            manufacturerName={match.manufacturer_name}
            compact
          />
          <ProductId match={match} />
        </div>
        <LoadingButton
          type="button"
          variant="outline"
          className="min-h-11 w-full sm:min-h-9 sm:w-auto"
          loading={pending}
          loadingText="Applying match..."
          disabled={disabled}
          onClick={onApply}
        >
          Apply {match.model}
        </LoadingButton>
      </div>
      {match.reason ? (
        <p className="mt-2 whitespace-pre-wrap break-words text-xs leading-5 text-muted-foreground">
          {match.reason}
        </p>
      ) : null}
      <dl
        aria-label={`Key pricing and fit for ${match.model}`}
        className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs sm:grid-cols-4"
      >
        {scanFacts.map(({ label, value }) => (
          <div key={label} className="min-w-0">
            <dt className="text-[11px] text-muted-foreground">{label}</dt>
            <dd className="mt-0.5 break-words font-semibold tabular-nums">
              {value}
            </dd>
          </div>
        ))}
      </dl>
      <div className="mt-2 border-t">
        <MatchDisclosure label="Evidence details" context={match.model}>
          <dl
            aria-label={`Additional catalog evidence for ${match.model}`}
            className="grid grid-cols-2 gap-3"
          >
            <div>
              <dt className="text-[11px] text-muted-foreground">List</dt>
              <dd className="mt-0.5 font-medium tabular-nums">
                {moneyLabel(match.list_price)}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] text-muted-foreground">Discount</dt>
              <dd className="mt-0.5 break-words font-medium">
                {match.discount_chain?.trim() || "—"}
              </dd>
            </div>
          </dl>
          <ReviewFlags flags={match.review_flags} />
          <FacetDifferences match={match} />
        </MatchDisclosure>
      </div>
    </article>
  );
}

function sameProduct(match: DropshipV2AIProductMatch, line: DropshipV2Line) {
  return (
    match.product_uuid === line.product_uuid ||
    (match.product_id != null && match.product_id === line.product_id)
  );
}

function lineMarginPercent(line: DropshipV2Line) {
  if (line.sell_price == null || line.net_price == null) return null;
  const sell = Number(line.sell_price);
  const net = Number(line.net_price);
  if (!Number.isFinite(sell) || !Number.isFinite(net) || sell <= 0) return null;
  return String(((sell - net) / sell) * 100);
}

function currentLineMatch(
  line: DropshipV2Line,
  evidence?: DropshipV2AIProductMatch,
): DropshipV2AIProductMatch | null {
  if (!line.product_uuid) return null;
  return {
    uuid: line.product_uuid,
    product_uuid: line.product_uuid,
    product_id: line.product_id,
    manufacturer_name: line.manufacturer_name || "Manufacturer needs review",
    model: line.model || "Current product",
    sku: line.sku,
    description: line.description,
    image_url: line.product_image_url,
    list_price: evidence?.list_price ?? line.list_price,
    net_price: evidence?.net_price ?? line.net_price,
    sell_price: evidence?.sell_price ?? line.sell_price,
    discount_chain: evidence?.discount_chain ?? line.discount_chain,
    margin_percent: evidence?.margin_percent ?? lineMarginPercent(line),
    confidence: evidence?.confidence ?? null,
    reason: evidence?.reason ?? null,
    facet_differences: evidence?.facet_differences ?? [],
    review_flags: evidence?.review_flags ?? [],
    configuration_recommendations:
      evidence?.configuration_recommendations ?? [],
    accessory_recommendations: evidence?.accessory_recommendations ?? [],
  };
}

export function DropshipLineMatchDialog({
  draftUuid,
  line,
  build,
  buildLoading,
  mutationsDisabled = false,
  open,
  onOpenChange,
}: {
  draftUuid: string;
  line: DropshipV2Line | null;
  build: DropshipV2AIBuild | null | undefined;
  buildLoading: boolean;
  mutationsDisabled?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const applyAlternative = useApplyDropshipV2AIAlternative(draftUuid);
  const isNarrow = useMediaQuery("(max-width: 1023px)");
  const lineChatPanelId = useId();
  const [lineChatOpen, setLineChatOpen] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applyNotice, setApplyNotice] = useState<string | null>(null);
  const lineAgent = line
    ? build?.line_agents.find((agent) => agent.line_uuid === line.uuid)
    : undefined;
  const currentEvidence = line
    ? [lineAgent?.selected_match, ...(lineAgent?.alternatives ?? [])].find(
        (match): match is DropshipV2AIProductMatch =>
          Boolean(match && sameProduct(match, line)),
      )
    : undefined;
  const selectedMatch = line
    ? (currentLineMatch(line, currentEvidence) ??
      lineAgent?.selected_match ??
      null)
    : null;
  const alternatives = (lineAgent?.alternatives ?? []).filter(
    (alternative) => !line || !sameProduct(alternative, line),
  );
  const lineStatus = lineAgent ? normalizedLineStatus(lineAgent) : null;
  const lineWorking =
    lineStatus && ["queued", "searching", "configuring"].includes(lineStatus);
  const lineChatExpanded = !isNarrow || lineChatOpen;

  const apply = (alternative: DropshipV2AIProductMatch) => {
    if (!line || !build || mutationsDisabled) return;
    setApplyError(null);
    setApplyNotice(null);
    applyAlternative.mutate(
      {
        buildUuid: build.uuid,
        lineUuid: line.uuid,
        alternative,
        expectedRevision: line.revision,
      },
      {
        onSuccess: () =>
          setApplyNotice(
            `${alternative.model} was applied to line ${line.display_number}.`,
          ),
        onError: (error) => {
          const stale =
            (error instanceof DropshipV2RequestError &&
              [409, 412].includes(error.status)) ||
            (error instanceof DropshipV2RequestError &&
              error.code?.includes("stale"));
          setApplyError(
            stale
              ? "This draft changed while the alternative was open. Review the refreshed line and try again."
              : dropshipV2ErrorMessage(
                  error,
                  "The alternative was not applied. Retry.",
                ),
          );
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          setApplyError(null);
          setApplyNotice(null);
        }
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent
        data-dropship-match-dialog="true"
        className="grid h-[calc(100dvh-1rem)] max-h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] grid-rows-[auto_minmax(0,1fr)] gap-0 overflow-hidden p-0 sm:max-w-5xl"
      >
        <DialogHeader className="border-b px-4 py-4 pr-12 sm:px-5">
          <DialogTitle className="text-base">
            Review match for line {line?.display_number ?? ""}
          </DialogTitle>
          <DialogDescription>
            Compare catalog evidence before replacing the selected product.
          </DialogDescription>
        </DialogHeader>
        <div
          data-match-dialog-layout={isNarrow ? "stacked" : "split"}
          className={cn(
            "grid min-h-0 overflow-hidden",
            isNarrow
              ? lineChatOpen
                ? "grid-rows-[minmax(0,1fr)_minmax(0,1fr)]"
                : "grid-rows-[minmax(0,1fr)_auto]"
              : "grid-cols-[minmax(0,3fr)_minmax(20rem,2fr)]",
          )}
        >
          <section
            aria-labelledby="line-match-options-title"
            aria-busy={buildLoading && !build}
            data-match-dialog-pane="products"
            className="min-h-0 min-w-0 space-y-4 overflow-y-auto overscroll-y-contain p-4 sm:p-5"
          >
            <h2 id="line-match-options-title" className="sr-only">
              Product match options
            </h2>
            {buildLoading && !build ? (
              <div className="space-y-3">
                <p role="status" className="sr-only">
                  Loading product matches...
                </p>
                <Skeleton className="h-40 w-full" />
                <Skeleton className="h-40 w-full" />
              </div>
            ) : (
              <>
                {lineAgent?.status === "failed" ? (
                  <p
                    role="alert"
                    className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
                  >
                    {lineAgent.error ||
                      lineAgent.error_message ||
                      "This line agent could not finish. Existing match details remain available."}
                  </p>
                ) : null}
                {selectedMatch ? (
                  <SelectedMatchSummary match={selectedMatch} />
                ) : (
                  <div className="rounded-lg border px-4 py-5 text-sm">
                    <p className="font-medium">No selected catalog match</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      The draft line remains unchanged while the agent searches
                      or awaits direction.
                    </p>
                  </div>
                )}

                <div>
                  <h3 className="mb-2 text-sm font-semibold">Alternatives</h3>
                  {mutationsDisabled ? (
                    <p className="mb-2 text-xs text-muted-foreground">
                      Match changes are locked after Sales approval. Catalog
                      evidence remains available for review.
                    </p>
                  ) : null}
                  {alternatives.length ? (
                    <ul
                      aria-label="Alternative product matches"
                      className="divide-y overflow-hidden rounded-lg border"
                    >
                      {alternatives.map((alternative) => (
                        <li key={matchKey(alternative)}>
                          <AlternativeMatchRow
                            match={alternative}
                            pending={
                              applyAlternative.isPending &&
                              matchKey(
                                applyAlternative.variables.alternative,
                              ) === matchKey(alternative)
                            }
                            disabled={
                              mutationsDisabled || applyAlternative.isPending
                            }
                            onApply={() => apply(alternative)}
                          />
                        </li>
                      ))}
                    </ul>
                  ) : lineWorking ? (
                    <div
                      role="status"
                      className="flex min-h-20 items-center gap-2 rounded-lg border px-4 text-sm text-muted-foreground"
                    >
                      <Loader2
                        className="size-4 animate-spin"
                        aria-hidden="true"
                      />
                      {lineStatus
                        ? LINE_STATUS_LABELS[lineStatus]
                        : "Searching"}{" "}
                      catalog candidates...
                    </div>
                  ) : (
                    <div className="rounded-lg border px-4 py-5 text-sm">
                      <p className="font-medium">No alternatives available</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Ask the line agent to widen the search or explain the
                        current selection.
                      </p>
                    </div>
                  )}
                </div>
                {applyError ? (
                  <p
                    role="alert"
                    className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
                  >
                    {applyError}
                  </p>
                ) : null}
                {applyNotice ? (
                  <p
                    role="status"
                    className="rounded-md border border-usa-blue/30 bg-usa-blue/10 px-3 py-2 text-xs text-usa-blue dark:border-usa-blue/50 dark:bg-usa-blue/20 dark:text-foreground"
                  >
                    {applyNotice}
                  </p>
                ) : null}
              </>
            )}
          </section>

          <section
            aria-labelledby="line-agent-chat-title"
            data-match-dialog-pane="chat"
            data-line-agent-state={lineStatus ?? "unavailable"}
            className={cn(
              "flex min-h-0 min-w-0 flex-col overflow-hidden border-t overscroll-y-contain",
              !isNarrow && "border-l border-t-0",
            )}
          >
            <div className="flex min-h-11 items-center gap-2 px-3">
              <Sparkles className="size-4 text-usa-blue" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <h2
                  id="line-agent-chat-title"
                  className="text-sm font-semibold"
                >
                  Line agent
                </h2>
                <p className="text-[11px] text-muted-foreground">
                  {lineStatus
                    ? `${LINE_STATUS_LABELS[lineStatus]} · Ask about evidence, alternatives, or configuration.`
                    : "Agent state unavailable · Build context has not loaded."}
                </p>
              </div>
              {isNarrow ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="min-h-11 shrink-0 gap-1 px-2"
                  aria-label={
                    lineChatOpen
                      ? "Hide line agent chat"
                      : "Open line agent chat"
                  }
                  aria-expanded={lineChatOpen}
                  aria-controls={lineChatPanelId}
                  onClick={() => setLineChatOpen((current) => !current)}
                >
                  {lineChatOpen ? "Hide" : "Open"}
                  <ChevronDown
                    className={cn("size-4", lineChatOpen && "rotate-180")}
                    aria-hidden="true"
                  />
                </Button>
              ) : null}
            </div>
            <div
              id={lineChatPanelId}
              hidden={!lineChatExpanded}
              className={cn(
                "min-h-0 flex-1 flex-col border-t",
                lineChatExpanded ? "flex" : "hidden",
                isNarrow &&
                  lineChatExpanded &&
                  "overflow-hidden overscroll-y-contain",
              )}
            >
              {line ? (
                <ContextualAgentChat
                  key={`${draftUuid}:${line.uuid}`}
                  draftUuid={draftUuid}
                  lineUuid={line.uuid}
                  conversationUuid={lineAgent?.conversation_uuid}
                  enabled={open && Boolean(build)}
                  refreshKey={lineAgent?.updated_at ?? build?.updated_at}
                />
              ) : null}
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DropshipQuoteAgent({
  draftUuid,
  build,
  lines,
  buildLoading,
  buildError,
  mutationsDisabled = false,
  open,
  explicitlyOpened,
  selectedLine,
  focusClarificationUuid,
  onClarificationFocused,
  onReviewRetainedEquipment,
  onOpenChange,
  onSelectedLineChange,
}: {
  draftUuid: string;
  build: DropshipV2AIBuild | null | undefined;
  lines: DropshipV2Line[];
  buildLoading: boolean;
  buildError: unknown;
  mutationsDisabled?: boolean;
  open: boolean;
  explicitlyOpened: boolean;
  selectedLine: DropshipV2Line | null;
  focusClarificationUuid?: string | null;
  onClarificationFocused?: () => void;
  onReviewRetainedEquipment?: () => void;
  onOpenChange: (open: boolean) => void;
  onSelectedLineChange: (line: DropshipV2Line | null) => void;
}) {
  const isMobile = useIsMobile();
  const showFloatingControl = Boolean(build || explicitlyOpened);
  const clarificationQuery = useDropshipV2Clarifications(draftUuid, true);
  const [answerDrafts, setAnswerDrafts] = useState<Record<string, string>>({});
  const [answerErrors, setAnswerErrors] = useState<Record<string, string>>({});
  const [clarificationAuthority, setClarificationAuthority] = useState<
    string | null
  >(null);
  const updateAnswerState = (
    setter: React.Dispatch<React.SetStateAction<Record<string, string>>>,
    key: string,
    value?: string,
  ) => {
    setter((current) => {
      if (value !== undefined) return { ...current, [key]: value };
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  if (clarificationQuery.data) {
    const nextAuthority = clarificationQuery.data.questions
      .map(
        (question) =>
          `${question.uuid}:${question.detected_revision}:${question.status}:${question.workflow_state}`,
      )
      .sort()
      .join("|");
    if (nextAuthority !== clarificationAuthority) {
      setClarificationAuthority(nextAuthority);
      const retainedKeys = new Set(
        clarificationQuery.data.questions
          .filter(
            (question) =>
              question.status === "open" &&
              ["awaiting_answer", "failed"].includes(question.workflow_state),
          )
          .map(
            (question) =>
              `${draftUuid}:${question.uuid}:${question.detected_revision}`,
          ),
      );
      const retainCurrentQuestions = (current: Record<string, string>) => {
        const retained = Object.fromEntries(
          Object.entries(current).filter(
            ([key]) =>
              !key.startsWith(`${draftUuid}:`) || retainedKeys.has(key),
          ),
        );
        const retainedEntries = Object.entries(retained);
        return retainedEntries.length === Object.keys(current).length &&
          retainedEntries.every(([key, value]) => current[key] === value)
          ? current
          : retained;
      };
      setAnswerDrafts(retainCurrentQuestions);
      setAnswerErrors(retainCurrentQuestions);
    }
  }

  return (
    <>
      {!open && showFloatingControl ? (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-[calc(1rem+env(safe-area-inset-right))] z-40 hidden size-12 rounded-full bg-background shadow-md lg:inline-flex"
          aria-label="Open quote agent"
          title="Open quote agent"
          onClick={() => onOpenChange(true)}
        >
          <Sparkles className="size-5 text-usa-blue" />
        </Button>
      ) : null}

      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side={isMobile ? "bottom" : "right"}
          data-quote-agent-layout={isMobile ? "mobile" : "desktop"}
          className={cn(
            "flex flex-col gap-0 overflow-hidden p-0 [&>button.absolute]:hidden",
            isMobile
              ? "h-[100dvh] w-full max-w-full border-x-0 sm:max-w-none"
              : "h-full w-full sm:max-w-[540px]",
          )}
        >
          <SheetHeader className="flex min-h-14 flex-row items-center gap-3 border-b px-4 py-2 text-left">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-md border bg-muted/30">
              <Sparkles className="size-4 text-usa-blue" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-base">Quote agent</SheetTitle>
              <SheetDescription className="truncate text-xs">
                Draft-scoped build and conversation
              </SheetDescription>
            </div>
            <SheetClose asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-11 shrink-0 sm:size-9"
                aria-label="Close quote agent"
              >
                <X className="size-4" />
              </Button>
            </SheetClose>
          </SheetHeader>
          <QuoteBuildProgress
            draftUuid={draftUuid}
            build={build}
            lines={lines}
            loading={buildLoading}
            error={buildError}
            onReviewRetainedEquipment={
              onReviewRetainedEquipment
                ? () => {
                    onReviewRetainedEquipment();
                    onOpenChange(false);
                  }
                : undefined
            }
          />
          <section
            aria-labelledby="quote-agent-chat-title"
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="flex min-h-10 items-center gap-2 border-b px-4">
              <MessageSquareText
                className="size-4 text-muted-foreground"
                aria-hidden="true"
              />
              <h2 id="quote-agent-chat-title" className="text-sm font-semibold">
                Draft conversation
              </h2>
            </div>
            <ContextualAgentChat
              key={`${draftUuid}:main`}
              draftUuid={draftUuid}
              conversationUuid={build?.conversation_uuid}
              enabled={open && Boolean(build)}
              refreshKey={build?.updated_at}
              lines={lines}
              answerDrafts={answerDrafts}
              answerErrors={answerErrors}
              focusClarificationUuid={focusClarificationUuid}
              onAnswerDraftChange={(key, value) =>
                updateAnswerState(setAnswerDrafts, key, value)
              }
              onAnswerErrorChange={(key, value) =>
                updateAnswerState(setAnswerErrors, key, value)
              }
              onClarificationFocused={onClarificationFocused}
              onSelectedLineChange={onSelectedLineChange}
            />
          </section>
        </SheetContent>
      </Sheet>

      <DropshipLineMatchDialog
        key={selectedLine?.uuid ?? "no-line"}
        draftUuid={draftUuid}
        line={selectedLine}
        build={build}
        buildLoading={buildLoading}
        mutationsDisabled={mutationsDisabled}
        open={Boolean(selectedLine)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) onSelectedLineChange(null);
        }}
      />
    </>
  );
}

export function QuoteAgentLineActionIcon() {
  return (
    <span className="relative block size-4" aria-hidden="true">
      <RefreshCw className="absolute inset-0 size-4" />
      <Sparkles className="absolute -right-1 -top-1 size-2.5 fill-background text-usa-blue" />
    </span>
  );
}
